import React, { useId, type ChangeEventHandler } from "react";

import cx from "@hsl/core/utils/cx";

import { Translate } from "../../hooks/useTranslation";

export interface Props<V> {
    containerClassName?: string;
    inputContainerClassName?: string;
    inputClassName?: string;
    labelClassName?: string;
    label?: React.ReactNode;
    value: V;
    selected?: boolean;
    disabled?: boolean;
    onChange?(val: V, checked: boolean): void;
}

const Checkbox = <V,>({
    containerClassName,
    inputContainerClassName,
    labelClassName,
    inputClassName,
    label,
    selected,
    value,
    disabled,
    onChange,
}: Props<V>) => {
    const id = useId();

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        e.stopPropagation();
        onChange && onChange(value, e.target.checked);
    };

    return (
        <div
            className={cx(
                "relative flex items-center py-[2px]",
                containerClassName,
            )}
        >
            <div
                className={cx(
                    "mr-2 flex h-6 items-center",
                    inputContainerClassName,
                )}
            >
                <input
                    id={id}
                    name={id}
                    type="checkbox"
                    checked={selected}
                    className={cx(
                        "text-core-blue focus:ring-core-blue h-3.5 w-3.5 cursor-pointer rounded border-gray-300",
                        inputClassName,
                    )}
                    onChange={handleChange}
                    disabled={disabled}
                    onClick={(evt) => evt.stopPropagation()}
                />
            </div>
            {label && (
                <div className="min-w-0 flex-1 leading-4">
                    <label
                        htmlFor={id}
                        className={cx(
                            "text-charcoal cursor-pointer select-none whitespace-break-spaces font-normal",
                            disabled && "text-warm-gray",
                            labelClassName,
                        )}
                    >
                        {typeof label === "string" ? (
                            <Translate>{label}</Translate>
                        ) : (
                            label
                        )}
                    </label>
                </div>
            )}
        </div>
    );
};

export default Checkbox;
